import { useMemo, type ReactNode } from 'react';
import { Checkbox } from './';

// ソート方向
export type SortDirection = 'asc' | 'desc' | null;

// ソート設定
export interface SortConfig {
  key: string;
  direction: SortDirection;
}

// カラム定義
export interface TableColumn<T> {
  /** カラムのキー（データのプロパティ名） */
  key: string;
  /** ヘッダーラベル */
  label: string;
  /** ソート可能かどうか */
  sortable?: boolean;
  /** ソートタイプ */
  sortType?: 'text' | 'number' | 'date';
  /** セルのレンダリング関数 */
  render?: (value: any, row: T, index: number) => ReactNode;
  /** ヘッダーのクラス名 */
  headerClassName?: string;
  /** セルのクラス名 */
  cellClassName?: string;
  /** 幅 */
  width?: string;
}

// テーブルプロパティ
export interface TableProps<T> {
  /** データ配列 */
  data: T[];
  /** カラム定義 */
  columns: TableColumn<T>[];
  /** 行のキーを取得する関数 */
  getRowKey: (row: T, index: number) => string | number;
  /** サイズ */
  size?: 'default' | 'dense';
  /** 行選択を有効にするか */
  selectable?: boolean;
  /** 選択された行のキー */
  selectedKeys?: (string | number)[];
  /** 選択変更時のコールバック */
  onSelectionChange?: (selectedKeys: (string | number)[]) => void;
  /** 選択時アクションバーに表示するボタン */
  selectionActions?: ReactNode;
  /** ソート設定 */
  sortConfig?: SortConfig;
  /** ソート変更時のコールバック */
  onSortChange?: (config: SortConfig) => void;
  /** 行クリック時のコールバック */
  onRowClick?: (row: T, index: number) => void;
  /** 操作カラムのレンダリング関数 */
  renderActions?: (row: T, index: number) => ReactNode;
  /** 空状態のメッセージ */
  emptyMessage?: string;
  /** 空状態の説明 */
  emptyDescription?: string;
  /** 空状態のアイコン */
  emptyIcon?: string;
  /** ヘッダーの上部に表示する要素 */
  headerContent?: ReactNode;
  /** フッターに表示する要素 */
  footerContent?: ReactNode;
  /** 追加のクラス名 */
  className?: string;
}

// ソートアイコンSVG
const SortIcon = () => (
  <svg className="aip-table__sort-svg" width="24" height="24" fill="currentColor" aria-hidden="true">
    <path d="M17 18.11L21.27 14L22 14.7L16.5 20L11 14.7L11.73 14L16 18.12V4H17V18.12ZM8 5.88L12.27 10L13 9.3L7.5 4L2 9.3L2.73 10L7 5.88V20H8V5.88Z" />
  </svg>
);

/**
 * テーブルコンポーネント
 */
export function Table<T>({
  data,
  columns,
  getRowKey,
  size = 'dense',
  selectable = false,
  selectedKeys = [],
  onSelectionChange,
  selectionActions,
  sortConfig,
  onSortChange,
  onRowClick,
  renderActions,
  emptyMessage = 'データがありません',
  emptyDescription,
  emptyIcon = 'inbox',
  headerContent,
  footerContent,
  className = '',
}: TableProps<T>) {
  // 全選択状態を計算
  const allSelected = useMemo(() => {
    if (data.length === 0) return false;
    return data.every((row, index) => selectedKeys.includes(getRowKey(row, index)));
  }, [data, selectedKeys, getRowKey]);

  const someSelected = useMemo(() => {
    if (data.length === 0) return false;
    const selectedCount = data.filter((row, index) => selectedKeys.includes(getRowKey(row, index))).length;
    return selectedCount > 0 && selectedCount < data.length;
  }, [data, selectedKeys, getRowKey]);

  // 全選択トグル
  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange?.([]);
    } else {
      const allKeys = data.map((row, index) => getRowKey(row, index));
      onSelectionChange?.(allKeys);
    }
  };

  // 行選択トグル
  const handleSelectRow = (key: string | number) => {
    if (selectedKeys.includes(key)) {
      onSelectionChange?.(selectedKeys.filter((k) => k !== key));
    } else {
      onSelectionChange?.([...selectedKeys, key]);
    }
  };

  // ソートハンドラー
  const handleSort = (key: string) => {
    if (!onSortChange) return;

    let direction: SortDirection = 'asc';
    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    onSortChange({ key, direction });
  };

  // 表示するカラム（操作カラムを含む）
  const displayColumns = useMemo(() => {
    if (renderActions) {
      return [...columns, { key: '__actions', label: '操作', sortable: false }];
    }
    return columns;
  }, [columns, renderActions]);

  return (
    <figure
      className={`aip-table-container ${className}`.trim()}
      data-size={size}
      data-selectable={selectable || undefined}
    >
      {headerContent && <div className="aip-table__header">{headerContent}</div>}

      {/* 選択時のアクションバー */}
      {selectable && selectedKeys.length > 0 && (
        <div className="aip-checked__function">
          <div className="aip-checked__item">{selectedKeys.length}件選択しました</div>
          {selectionActions}
        </div>
      )}

      <div className="aip-table">
        <table
          className="aip-table__table"
          data-cell-border="bottom"
          data-width="full"
          data-header-color="false"
        >
          <thead>
            <tr>
              {selectable && (
                <th className="aip-table__col-header" scope="col">
                  <Checkbox
                    size="sm"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    ref={(el) => {
                      if (el) {
                        const input = el.querySelector('input');
                        if (input) {
                          input.indeterminate = someSelected;
                        }
                      }
                    }}
                  />
                </th>
              )}
              {displayColumns.map((col) => {
                if (col.key === '__actions') {
                  return (
                    <th key={col.key} className="aip-table__col-header aip-table__col-header--action" scope="col">
                      {col.label}
                    </th>
                  );
                }

                if (col.sortable) {
                  return (
                    <th
                      key={col.key}
                      className={`aip-table__sort-header ${col.headerClassName || ''}`.trim()}
                      scope="col"
                      data-sort-type={col.sortType || 'text'}
                    >
                      <div className="aip-table__sort-inner">
                        <div className="aip-table__sort-label">
                          <button
                            type="button"
                            className="aip-table__sort-button"
                            onClick={() => handleSort(col.key)}
                          >
                            {col.label}
                            <span className="aip-table__sort-icon">
                              <SortIcon />
                            </span>
                          </button>
                        </div>
                      </div>
                    </th>
                  );
                }

                return (
                  <th
                    key={col.key}
                    className={`aip-table__col-header ${col.headerClassName || ''}`.trim()}
                    scope="col"
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  className="aip-table__empty-cell"
                  colSpan={displayColumns.length + (selectable ? 1 : 0)}
                >
                  <div className="aip-table__empty-state">
                    <span className="material-symbols-outlined aip-table__empty-icon">{emptyIcon}</span>
                    <div className="aip-table__empty-text">
                      <p className="aip-table__empty-message">{emptyMessage}</p>
                      {emptyDescription && (
                        <p className="aip-table__empty-description">{emptyDescription}</p>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowKey = getRowKey(row, rowIndex);
                const isSelected = selectedKeys.includes(rowKey);

                return (
                  <tr
                    key={rowKey}
                    onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {selectable && (
                      <td>
                        <Checkbox
                          size="sm"
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowKey)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                    )}
                    {displayColumns.map((col) => {
                      if (col.key === '__actions') {
                        return (
                          <td key={col.key} className="aip-table__cell--action">
                            <div className="aip-table__action-buttons">
                              {renderActions?.(row, rowIndex)}
                            </div>
                          </td>
                        );
                      }

                      const value = (row as any)[col.key];
                      const content = col.render ? col.render(value, row, rowIndex) : value;

                      return (
                        <td
                          key={col.key}
                          className={col.cellClassName}
                          data-sort-type={col.sortType || undefined}
                        >
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {footerContent && <div className="aip-table__footer">{footerContent}</div>}
    </figure>
  );
}

export default Table;

